// `principalFromClaims` — derives the SPA-facing `Principal` shape
// from the auth token's `CustomClaims` and the Firebase Auth user
// object. Pure; no SDK calls. Lives here (not in `apps/web/`) so the
// claim-sync triggers' tests in `functions/` can reuse the same
// derivation when constructing fixture principals for assertions.
//
// Inputs:
//   - `claims`: the decoded custom-claims payload as written by the
//     sync triggers. May be `null` for an anonymous / signed-out
//     principal, or for a signed-in user whose claims haven't been
//     stamped yet (first sign-in race; the trigger writes claims
//     post-create, the SDK refreshes on the next call).
//   - `typedEmail`: the email string the Firebase Auth user object
//     carries. Claims hold the canonical form; the UI wants the typed
//     form for display. Pass the `email` field from the user record.
//
// Output:
//   A fully-populated `Principal` with `isAuthenticated` reflecting
//   whether the user has *any* role at all — a signed-in user who
//   resolves to no role lands on the NotAuthorized page.
/**
 * Compute the {@link Principal} shape from a custom-claims payload + the
 * typed email of the signed-in Firebase Auth user.
 *
 * Returns an unauthenticated principal (all flags false / arrays empty)
 * when `claims` is null OR when the user has no claims at all (no
 * superadmin flag, no stake claims).
 */
export function principalFromClaims(claims, typedEmail) {
    const email = typedEmail ?? '';
    if (!claims || typeof claims.canonical !== 'string' || claims.canonical === '') {
        return emptyPrincipal(email);
    }
    const isPlatformSuperadmin = claims.isPlatformSuperadmin === true;
    const managerStakes = [];
    const stakeMemberStakes = [];
    const bishopricWards = {};
    const limitedStakes = [];
    const bootstrapStakes = [];
    if (claims.stakes) {
        for (const [stakeId, stake] of Object.entries(claims.stakes)) {
            const s = normaliseStakeClaims(stake);
            if (s.manager)
                managerStakes.push(stakeId);
            if (s.stake)
                stakeMemberStakes.push(stakeId);
            if (s.wards.length > 0)
                bishopricWards[stakeId] = [...s.wards];
            // `limited` narrows what an existing role may do; it is not a
            // role itself, so it stays out of the `hasAnyRole` test below.
            if (s.limited === true)
                limitedStakes.push(stakeId);
            // `bootstrap` grants no access — it only makes the stake
            // discoverable pre-setup. Stays out of `hasAnyRole` too: a
            // bootstrap-only principal must keep reading as unauthenticated
            // until the wizard's auto-add mints a real manager claim.
            if (s.bootstrap === true)
                bootstrapStakes.push(stakeId);
        }
    }
    const hasAnyRole = isPlatformSuperadmin ||
        managerStakes.length > 0 ||
        stakeMemberStakes.length > 0 ||
        Object.keys(bishopricWards).length > 0;
    return {
        email,
        canonical: claims.canonical,
        isAuthenticated: hasAnyRole,
        isPlatformSuperadmin,
        managerStakes,
        stakeMemberStakes,
        bishopricWards,
        limitedStakes,
        bootstrapStakes,
    };
}
function emptyPrincipal(email) {
    return {
        email,
        canonical: '',
        isAuthenticated: false,
        isPlatformSuperadmin: false,
        managerStakes: [],
        stakeMemberStakes: [],
        bishopricWards: {},
        limitedStakes: [],
        bootstrapStakes: [],
    };
}
function normaliseStakeClaims(raw) {
    if (!raw || typeof raw !== 'object') {
        return { manager: false, stake: false, wards: [] };
    }
    const r = raw;
    return {
        manager: r.manager === true,
        stake: r.stake === true,
        wards: Array.isArray(r.wards) ? r.wards.filter((w) => typeof w === 'string') : [],
        // Omit-when-false, matching what the claim writer stores: a
        // normalised block never carries `limited: false`, so it can be
        // compared against a live token without reporting a spurious diff.
        // Anything but a literal `true` — absent, `false`, a truthy string
        // off the wire — reads as full access.
        ...(r.limited === true ? { limited: true } : {}),
        // Same omit-when-false convention as `limited`. A block whose only
        // content is `bootstrap: true` must still round-trip here — it's
        // not "no roles", it's a discoverable pre-setup marker.
        ...(r.bootstrap === true ? { bootstrap: true } : {}),
    };
}
//# sourceMappingURL=principal.js.map